import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Plus, Pencil, Trash2, Factory, ChevronRight, ChevronDown, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';

const AssetMakesModelsTab = ({ api, makes, models, fetchMakes, fetchModels }) => {
  // Make state
  const [makeDialogOpen, setMakeDialogOpen] = useState(false);
  const [editingMake, setEditingMake] = useState(null);
  const [makeForm, setMakeForm] = useState({ name: '', is_active: true });
  const [deletingMake, setDeletingMake] = useState(null);

  // Model state
  const [modelDialogOpen, setModelDialogOpen] = useState(false);
  const [editingModel, setEditingModel] = useState(null);
  const [modelForm, setModelForm] = useState({ name: '', make_id: '', is_active: true });
  const [deletingModel, setDeletingModel] = useState(null);

  // Expanded makes (to show their models)
  const [expandedMakes, setExpandedMakes] = useState(new Set());

  // Toggle make expansion
  const toggleMakeExpand = (makeId) => {
    const newExpanded = new Set(expandedMakes);
    if (newExpanded.has(makeId)) {
      newExpanded.delete(makeId);
    } else {
      newExpanded.add(makeId);
    }
    setExpandedMakes(newExpanded);
  };

  // Get models for a specific make
  const getModelsForMake = (makeId) => {
    return models.filter(m => m.make_id === makeId);
  };

  // ==================== MAKE HANDLERS ====================

  const handleOpenMakeDialog = (make = null) => {
    if (make) {
      setEditingMake(make);
      setMakeForm({ name: make.name, is_active: make.is_active ?? true });
    } else {
      setEditingMake(null);
      setMakeForm({ name: '', is_active: true });
    }
    setMakeDialogOpen(true);
  };

  const handleSaveMake = async (e) => {
    e.preventDefault();
    try {
      if (editingMake) {
        await api.put(`/equipment-makes/${editingMake.id}`, makeForm);
        toast.success('Make updated');
      } else {
        await api.post('/equipment-makes', makeForm);
        toast.success('Make created');
      }
      setMakeDialogOpen(false);
      fetchMakes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save make');
    }
  };

  const handleDeleteMake = async () => {
    if (!deletingMake) return;
    try {
      await api.delete(`/equipment-makes/${deletingMake.id}`);
      toast.success('Make deleted');
      setDeletingMake(null);
      fetchMakes();
      fetchModels(); // Models under this make are also deleted
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete make');
    }
  };

  const handleToggleMakeActive = async (make) => {
    try {
      await api.put(`/equipment-makes/${make.id}`, {
        name: make.name,
        is_active: !make.is_active
      });
      toast.success(`Make ${make.is_active ? 'deactivated' : 'activated'}`);
      fetchMakes();
    } catch (error) {
      toast.error('Failed to update make status');
    }
  };

  // ==================== MODEL HANDLERS ====================

  const handleOpenModelDialog = (model = null, defaultMakeId = null) => {
    if (model) {
      setEditingModel(model);
      setModelForm({ name: model.name, make_id: model.make_id, is_active: model.is_active ?? true });
    } else {
      setEditingModel(null);
      setModelForm({ name: '', make_id: defaultMakeId || '', is_active: true });
    }
    setModelDialogOpen(true);
  };

  const handleSaveModel = async (e) => {
    e.preventDefault();
    if (!modelForm.make_id) {
      toast.error('Please select a make');
      return;
    }
    try {
      if (editingModel) {
        await api.put(`/equipment-models/${editingModel.id}`, modelForm);
        toast.success('Model updated');
      } else {
        await api.post('/equipment-models', modelForm);
        toast.success('Model created');
      }
      setModelDialogOpen(false);
      fetchModels();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save model');
    }
  };

  const handleDeleteModel = async () => {
    if (!deletingModel) return;
    try {
      await api.delete(`/equipment-models/${deletingModel.id}`);
      toast.success('Model deleted');
      setDeletingModel(null);
      fetchModels();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete model');
    }
  };

  const handleToggleModelActive = async (model) => {
    try {
      await api.put(`/equipment-models/${model.id}`, {
        name: model.name,
        make_id: model.make_id,
        is_active: !model.is_active
      });
      toast.success(`Model ${model.is_active ? 'deactivated' : 'activated'}`);
      fetchModels();
    } catch (error) {
      toast.error('Failed to update model status');
    }
  };

  return (
    <div className="space-y-6">
      {/* Makes Section */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Factory className="h-5 w-5" />
              Equipment Makes
            </CardTitle>
            <CardDescription>
              Manage equipment manufacturers (e.g., Caterpillar, Liebherr, Kenworth)
            </CardDescription>
          </div>
          <Button onClick={() => handleOpenMakeDialog()} data-testid="add-make-btn">
            <Plus className="h-4 w-4 mr-2" />
            Add Make
          </Button>
        </CardHeader>
        <CardContent>
          {makes.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Factory className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No equipment makes defined yet.</p>
              <p className="text-sm">Add makes to categorize your equipment by manufacturer.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10"></TableHead>
                  <TableHead>Make Name</TableHead>
                  <TableHead>Models</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {makes.map((make) => {
                  const makeModels = getModelsForMake(make.id);
                  const isExpanded = expandedMakes.has(make.id);
                  
                  return (
                    <>
                      <TableRow key={make.id} className="group">
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0"
                            onClick={() => toggleMakeExpand(make.id)}
                          >
                            {isExpanded ? (
                              <ChevronDown className="h-4 w-4" />
                            ) : (
                              <ChevronRight className="h-4 w-4" />
                            )}
                          </Button>
                        </TableCell>
                        <TableCell className="font-medium">{make.name}</TableCell>
                        <TableCell>
                          <Badge variant="secondary">
                            {makeModels.length} model{makeModels.length !== 1 ? 's' : ''}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Switch
                              checked={make.is_active !== false}
                              onCheckedChange={() => handleToggleMakeActive(make)}
                            />
                            <span className="text-sm text-muted-foreground">
                              {make.is_active !== false ? 'Active' : 'Inactive'}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleOpenModelDialog(null, make.id)}
                              data-testid={`add-model-${make.id}`}
                            >
                              <Plus className="h-3 w-3 mr-1" />
                              Add Model
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleOpenMakeDialog(make)}
                              data-testid={`edit-make-${make.id}`}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setDeletingMake(make)}
                              data-testid={`delete-make-${make.id}`}
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                      
                      {/* Expanded Models */}
                      {isExpanded && makeModels.length > 0 && makeModels.map((model) => (
                        <TableRow key={model.id} className="bg-muted/30 group">
                          <TableCell></TableCell>
                          <TableCell className="pl-8">
                            <span className="text-muted-foreground">└</span>
                            <span className="ml-2">{model.name}</span>
                          </TableCell>
                          <TableCell></TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Switch
                                checked={model.is_active !== false}
                                onCheckedChange={() => handleToggleModelActive(model)}
                              />
                              <span className="text-sm text-muted-foreground">
                                {model.is_active !== false ? 'Active' : 'Inactive'}
                              </span>
                            </div>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleOpenModelDialog(model)}
                                data-testid={`edit-model-${model.id}`}
                              >
                                <Pencil className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setDeletingModel(model)}
                                data-testid={`delete-model-${model.id}`}
                              >
                                <Trash2 className="h-4 w-4 text-destructive" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                      
                      {/* Empty models message */}
                      {isExpanded && makeModels.length === 0 && (
                        <TableRow className="bg-muted/30">
                          <TableCell colSpan={5} className="text-center text-muted-foreground py-4">
                            No models for this make.{' '}
                            <Button
                              variant="link"
                              className="p-0 h-auto"
                              onClick={() => handleOpenModelDialog(null, make.id)}
                            >
                              Add one
                            </Button>
                          </TableCell>
                        </TableRow>
                      )}
                    </>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Make Dialog */}
      <Dialog open={makeDialogOpen} onOpenChange={setMakeDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingMake ? 'Edit Make' : 'Add New Make'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveMake} className="space-y-4">
            <div className="space-y-2">
              <Label>Make Name <span className="text-red-500">*</span></Label>
              <Input
                value={makeForm.name}
                onChange={(e) => setMakeForm({ ...makeForm, name: e.target.value })}
                placeholder="e.g., Caterpillar, Liebherr, Kenworth"
                required
                data-testid="make-name-input"
              />
            </div>
            <div className="flex items-center gap-2">
              <Switch
                checked={makeForm.is_active}
                onCheckedChange={(checked) => setMakeForm({ ...makeForm, is_active: checked })}
              />
              <Label>Active</Label>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setMakeDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" data-testid="save-make-btn">
                {editingMake ? 'Update' : 'Create'} Make
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Model Dialog */}
      <Dialog open={modelDialogOpen} onOpenChange={setModelDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingModel ? 'Edit Model' : 'Add New Model'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveModel} className="space-y-4">
            <div className="space-y-2">
              <Label>Make <span className="text-red-500">*</span></Label>
              <Select
                value={modelForm.make_id}
                onValueChange={(value) => setModelForm({ ...modelForm, make_id: value })}
                disabled={!!editingModel}
              >
                <SelectTrigger data-testid="model-make-select">
                  <SelectValue placeholder="Select make" />
                </SelectTrigger>
                <SelectContent>
                  {makes.filter(m => m.is_active !== false).map((make) => (
                    <SelectItem key={make.id} value={make.id}>{make.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Model Name <span className="text-red-500">*</span></Label>
              <Input
                value={modelForm.name}
                onChange={(e) => setModelForm({ ...modelForm, name: e.target.value })}
                placeholder="e.g., 320D, LTM 1060, T909"
                required
                data-testid="model-name-input"
              />
            </div>
            <div className="flex items-center gap-2">
              <Switch
                checked={modelForm.is_active}
                onCheckedChange={(checked) => setModelForm({ ...modelForm, is_active: checked })}
              />
              <Label>Active</Label>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setModelDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" data-testid="save-model-btn">
                {editingModel ? 'Update' : 'Create'} Model
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Make Confirmation */}
      <AlertDialog open={!!deletingMake} onOpenChange={() => setDeletingMake(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-destructive" />
              Delete Make
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>{deletingMake?.name}</strong>?
              {getModelsForMake(deletingMake?.id || '').length > 0 && (
                <span className="block mt-2 text-destructive">
                  This will also delete {getModelsForMake(deletingMake?.id || '').length} associated model(s).
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteMake} className="bg-destructive text-destructive-foreground">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Model Confirmation */}
      <AlertDialog open={!!deletingModel} onOpenChange={() => setDeletingModel(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-destructive" />
              Delete Model
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>{deletingModel?.name}</strong>?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteModel} className="bg-destructive text-destructive-foreground">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AssetMakesModelsTab;
